import React, { useState, useEffect } from 'react';
 
import tabsConfig, { tabComponents } from '../config/tabsConfig';
import { useTranslation } from '../utils/languageUtils';
import { getGlobalSelectedOptions } from '../utils/globalOptions';

const NavbarPanel = ({ activeTab, setActiveTab, product, products, onProductSelect, konvaController, canvasContainerRef, configData, onShowThemeSelection, selectedTheme, onThemeSelect, onShowLayerModal }) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Update mobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const computeCanPreview = () => {
    try {
      const options = configData?.SupportedModels?.Options || {};
      const batchEnabled = !!(configData && configData.BatchEnable);
      const qtyDefKey = Object.keys(options).find(k => options[k]?.qty || options[k]?.total_qty) || 'Option1';
      const totalCfg = options[qtyDefKey]?.qty || options[qtyDefKey]?.total_qty || {};
      const hasQtyCfg = !!(options[qtyDefKey]?.qty || options[qtyDefKey]?.total_qty);
      const sum = Number(totalCfg?.sum);
      const min = Number(totalCfg?.min);
      const max = Number(totalCfg?.max);
      const findKeyByName = (n) => {
        const nn = String(n || '').toLowerCase();
        for (const [k, def] of Object.entries(options)) {
          const nm = String(def?.name || '').toLowerCase();
          if (nm === nn) return k;
          const dn = typeof def?.displayName === 'string' ? def.displayName : (def?.displayName?.en || def?.displayName?.cn || '');
          if (String(dn).toLowerCase() === nn) return k;
        }
        return null;
      };
      const colorKey = findKeyByName('color') || Object.keys(options).find(k => {
        const d = options[k];
        const method = String(d?.displayMethod || '').toLowerCase();
        return d?.isVisualOption === true && method.includes('colorswatch');
      }) || null;
      const sizeKey = findKeyByName('size') || null;
      const requireExact = (!batchEnabled) && !!colorKey && !sizeKey;
      if (sizeKey && !batchEnabled) {
        return true;
      }
      if (!hasQtyCfg) {
        return true;
      }
      const batch = (getGlobalSelectedOptions()?.BatchQtyMatrix?.value) || null;
      const colorOnly = (getGlobalSelectedOptions()?.ColorQtyMap?.value) || null;
      let total = 0;
      if (batchEnabled && sizeKey && batch && typeof batch === 'object') {
        total = Object.values(batch).reduce((s, n) => s + (Number(n) || 0), 0);
      } else if (colorOnly && typeof colorOnly === 'object') {
        total = Object.values(colorOnly).reduce((s, n) => s + (Number(n) || 0), 0);
      } else {
        return false;
      }
      if (!isNaN(sum)) return total === sum;
      if (requireExact && !isNaN(max)) return total === max;
      if (!isNaN(min) && !isNaN(max)) return total >= min && total <= max;
      if (!isNaN(min)) return total >= min;
      if (!isNaN(max)) return total <= max;
      return total > 0;
    } catch {
      return false;
    }
  };

  const [canPreview, setCanPreview] = useState(false);
  useEffect(() => {
    const update = () => setCanPreview(computeCanPreview());
    update();
    const onChange = () => update();
    window.addEventListener('globalOptionsChanged', onChange);
    return () => window.removeEventListener('globalOptionsChanged', onChange);
  }, [configData]);

  const handlePreviewClick = () => {
    if (!canPreview) return;
    window.dispatchEvent(new CustomEvent('REQUEST_DOWNLOAD_PREVIEW'));
  };
  // Find the active tab config or use default
  
  const activeTabConfig = tabsConfig.find(tab => tab.id === activeTab) || {
    title: 'Select an option',
    description: 'Please select an option from the sidebar to get started.'
  };

  // Get the component for the active tab, or null if not found
  let ActiveTabComponent = tabComponents[activeTab] || null;

  // Clone the active tab component with all necessary props
  const renderActiveTab = () => {
    if (!ActiveTabComponent) return null;
    
    const tabProps = {
      product,
      products,
      onProductSelect,
      konvaController,
      canvasContainerRef,
      configData, // Pass the configData to the tab components
      onShowThemeSelection,
      selectedTheme,
      onThemeSelect,
      setActiveTab,
      onShowLayerModal,
      tabConfig: activeTabConfig
    };
    
    return React.createElement(ActiveTabComponent, tabProps);
  };

  // Toggle tab function for the icon
  const toggleTab = (tabId) => {
    // If clicking the currently active tab or any tab is active, close the panel
    if (activeTab) {
      // If panel is open, always close it first
      setActiveTab(null);
    } else {
      // Only open the panel if it's currently closed
      setActiveTab(tabId);
    }
  };

  return (
    <div className="navbar-panel">
      {activeTab !== 'toolbox' && (
        <div className="mobile-navbar-header">
          <div className="mobile-header-row">
            <h3 className="mobile-navbar-title">
              {activeTabConfig.title ? t(activeTabConfig.title, activeTabConfig.title) : 'Menu'}
            </h3>
            {/* Down arrow that toggles panel on mobile */}
            <div 
              className="down-arrow-icon" 
              onClick={() => toggleTab('customize')} /* Default to customize tab */
              title="Toggle Options Panel"
            >&#9660;</div>
          </div>
        </div>
      )}
            {/* Preview button - Only show in tool tab for mobile */}
      {isMobile && activeTab === 'toolbox' && (
        <div className="navbar-panel-footer">
          <button 
            className="add-to-cart-button"
            onClick={handlePreviewClick}
            disabled={!canPreview}
            aria-disabled={!canPreview}
            tabIndex={canPreview ? 0 : -1}
          >
            {t('preview', 'Preview')}
          </button>
        </div>
      )}
      <div className="navbar-panel-content">
        {/* Render the active tab component if it exists */}
        {ActiveTabComponent && (
          <div className={`${activeTab}-tab-content`}>
            {renderActiveTab()}
          </div>
        )}
      </div>
            {/* Preview button for desktop */}
      {activeTab && (!isMobile) && (
        <div className="navbar-panel-footer">
          <button 
            className="add-to-cart-button"
            onClick={handlePreviewClick}
            disabled={!canPreview}
            aria-disabled={!canPreview}
            tabIndex={canPreview ? 0 : -1}
          >
            {t('preview', 'Preview')}
          </button>
        </div>
      )}
      

    </div>
  );
};

export default NavbarPanel;
